/**
 * Cart Note Limiter
 * ------------------------------------------------------------------
 * Enforces a strict character limit on all Shopify cart note / order
 * note textareas across:
 *   - Cart Page      (#cart-note, in sections/main-cart.liquid)
 *   - Cart Drawer    (#Cart-note, in snippets/halo-side-cart.liquid —
 *                    AJAX-loaded into .previewCart-wrapper)
 *
 * Features:
 *   1. Hard 30-char limit via the native maxlength attribute.
 *   2. Real-time remaining-char counter ("N characters remaining").
 *   3. Friendly error message when the limit is reached / exceeded
 *      ("Note is too long. Please keep it within 30 characters."), with a
 *      brief flash animation when the user tries to keep typing at the limit.
 *   4. Paste handling: long pastes are auto-truncated by maxlength and
 *      the error message is shown.
 *   5. MutationObserver picks up the AJAX-injected cart drawer textarea
 *      so the limiter attaches automatically, no manual re-init needed.
 *
 * Because Shopify's native checkout page is not editable via theme
 * code (standard plan, no checkout.liquid), the limit enforced here
 * on the cart page and cart drawer guarantees the note that reaches
 * checkout is already ≤ 30 characters.
 */
(function () {
  'use strict';

  var MAX_CHARS = 30;
  var COUNTER_TEMPLATE = '{n} characters remaining';
  var ERROR_MSG = 'Note is too long. Please keep it within 30 characters.';

  /* Inject styles once */
  var style = document.createElement('style');
  style.textContent = [
    '.cart-note-limiter__counter{display:block;font-size:12px;margin-top:6px;color:#888;line-height:1.4;}',
    '.cart-note-limiter__counter.is-warn{color:#e67e22;font-weight:600;}',
    '.cart-note-limiter__counter.is-limit{color:#e74c3c;font-weight:600;}',
    '.cart-note-limiter__error{display:none;font-size:12px;margin-top:4px;color:#e74c3c;font-weight:500;line-height:1.4;}',
    '.cart-note-limiter__error.is-show{display:block;}',
    '.cart-note-limiter__error.is-flash{animation:cartNoteShake 0.4s ease;}',
    '@keyframes cartNoteShake{0%,100%{transform:translateX(0);}25%{transform:translateX(-3px);}75%{transform:translateX(3px);}}'
  ].join('\n');
  (document.head || document.documentElement).appendChild(style);

  /**
   * Attach the limiter (counter + error + listeners) to a single textarea.
   * Guarded by a data flag so it never double-initialises the same element
   * (important because the MutationObserver may re-scan).
   */
  function applyLimiter(textarea) {
    if (!textarea || textarea.dataset.cartNoteLimiter === 'true') return;
    textarea.dataset.cartNoteLimiter = 'true';

    /* 1. Hard limit at the browser level */
    textarea.setAttribute('maxlength', String(MAX_CHARS));

    /* 2. Build the counter element */
    var counter = document.createElement('span');
    counter.className = 'cart-note-limiter__counter';

    /* 3. Build the error element */
    var error = document.createElement('span');
    error.className = 'cart-note-limiter__error';
    error.textContent = ERROR_MSG;

    /* Insert counter + error right after the textarea */
    if (textarea.parentNode) {
      textarea.parentNode.insertBefore(error, textarea.nextSibling);
      textarea.parentNode.insertBefore(counter, error);
    }

    function update() {
      var len = textarea.value.length;
      var remaining = MAX_CHARS - len;
      counter.textContent = COUNTER_TEMPLATE.replace('{n}', remaining);
      counter.classList.toggle('is-warn', remaining > 0 && remaining <= 5);
      counter.classList.toggle('is-limit', remaining <= 0);
      error.classList.toggle('is-show', remaining <= 0);
    }

    /* Real-time counter */
    textarea.addEventListener('input', update);

    /* Paste: browser truncates via maxlength, then we re-check */
    textarea.addEventListener('paste', function () {
      setTimeout(update, 0);
    });

    /* When already at the limit and the user tries to keep typing,
       flash the error for extra feedback (character is blocked by maxlength) */
    textarea.addEventListener('keydown', function (e) {
      var remaining = MAX_CHARS - textarea.value.length;
      if (remaining <= 0 && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        error.classList.add('is-flash');
        setTimeout(function () { error.classList.remove('is-flash'); }, 400);
      }
    });

    /* Initial state */
    update();
  }

  function scanAll(root) {
    var textareas = (root || document).querySelectorAll('textarea[name="note"]');
    for (var i = 0; i < textareas.length; i++) {
      applyLimiter(textareas[i]);
    }
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scanAll(); });
  } else {
    scanAll();
  }

  /* Watch for AJAX-injected cart drawer content */
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'TEXTAREA' && node.getAttribute('name') === 'note') {
            applyLimiter(node);
          }
          if (node.querySelectorAll) {
            scanAll(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
